 
/* eslint-disable @typescript-eslint/no-unused-vars */
import { inventoryManagementService } from './inventoryManagementService';
import { factoryProductionService } from './factoryProductionService';
import { GAME_FORMULAS } from '@/config/gameFormulas';
import { generateId } from '@/db/connection';
import type { 
  ProductionQueue, 
  ManufacturingConstraints, 
  WorkInProgress,
  ProductionLine 
} from '@/types/production';
import type { Product, Employee } from '@/types/tycoon';
import type { OwnedBuilding } from '@/types/buildings';

export class ManufacturingService {
  private static instance: ManufacturingService;
  private productionQueue: Map<string, ProductionQueue> = new Map();
  private workInProgress: Map<string, WorkInProgress> = new Map();
  
  private constructor() {}
  
  static getInstance(): ManufacturingService {
    if (!ManufacturingService.instance) {
      ManufacturingService.instance = new ManufacturingService();
    }
    return ManufacturingService.instance;
  }

  // Check if manufacturing can start for a product
  async checkManufacturingConstraints(
    product: Product,
    quantity: number,
    productionLine: ProductionLine | null,
    employees: Employee[],
    buildings: OwnedBuilding[]
  ): Promise<ManufacturingConstraints> {
    const constraints: ManufacturingConstraints = {
      canProduce: true,
      hasComponents: true,
      hasWorkers: true,
      hasCapacity: true,
      hasProductionLine: !!productionLine
    };

    // Check if product has a production line assigned
    if (!productionLine) {
      constraints.canProduce = false;
      constraints.hasProductionLine = false;
      constraints.message = 'Product must be assigned to a production line before manufacturing';
      return constraints;
    }

    // Check if device inventory exists
    const deviceInventory = await inventoryManagementService.getDeviceInventoryByProductId(product.id);
    if (!deviceInventory) {
      constraints.canProduce = false;
      constraints.message = 'Device inventory not initialized for this product';
      return constraints;
    }

    // Check component availability - get from inventory service
    if (product.designConfig?.components) {
      const missingComponents = new Map<string, { required: number; available: number }>();
      
      // Get current inventory state
      const inventoryState = await inventoryManagementService.initializeInventory([product]);
      const componentInventory = inventoryState.components;
      
      for (const [category, componentId] of Object.entries(product.designConfig.components)) {
        const required = quantity; // 1:1 ratio as specified
        const inventory = componentInventory.get(componentId);
        const available = inventory?.currentStock || 0;
        
        if (available < required) {
          constraints.hasComponents = false;
          missingComponents.set(componentId, { required, available });
        }
      }
      
      if (!constraints.hasComponents) {
        constraints.canProduce = false;
        constraints.missingComponents = missingComponents;
        constraints.message = 'Insufficient components for production';
      }
    }

    // Check worker availability - use production line's allocated workers
    if (productionLine && productionLine.allocatedEmployees > 0) {
      // Production line has workers allocated
      constraints.hasWorkers = true;
      constraints.workersAvailable = productionLine.allocatedEmployees;
    } else {
      // No workers allocated to the production line
      const assemblyWorkers = employees.filter(e => e.role === 'assemblyWorker');
      const allocatedWorkers = this.getAllocatedWorkers();
      const availableWorkers = assemblyWorkers.length - allocatedWorkers;
      const workersNeeded = Math.max(1, Math.ceil(quantity / 1000)); // 1 worker per 1000 units
      
      constraints.hasWorkers = false;
      constraints.canProduce = false;
      constraints.workersNeeded = workersNeeded;
      constraints.workersAvailable = availableWorkers;
      constraints.message = productionLine ? 
        `No workers allocated to production line` : 
        `Need ${workersNeeded} workers, only ${availableWorkers} available`;
    }

    // Check factory capacity
    const factory = buildings.find(b => b.id === productionLine.factoryBuildingId);
    if (factory) {
      const currentCapacityUsed = this.getFactoryCapacityUsage(factory.id);
      const maxCapacity = 100; // 100% is normal capacity
      
      constraints.capacityUsed = currentCapacityUsed;
      constraints.maxCapacity = maxCapacity;
      
      if (currentCapacityUsed >= 150) { // Hard limit at 150%
        constraints.hasCapacity = false;
        constraints.canProduce = false;
        constraints.message = 'Factory at maximum capacity (150%)';
      }
    }
    
    // console.log('[Manufacturing] 📊 Final constraint check result:', {
    //   productId: product.id,
    //   canProduce: constraints.canProduce,
    //   hasComponents: constraints.hasComponents,
    //   hasWorkers: constraints.hasWorkers,
    //   hasCapacity: constraints.hasCapacity,
    //   hasProductionLine: constraints.hasProductionLine,
    //   message: constraints.message
    // });
    
    return constraints;
  }

  // Start manufacturing a batch of products
  async startManufacturing(
    product: Product,
    quantity: number,
    productionLineId: string,
    gameDate: { day: number; month: number; year: number }
  ): Promise<{ success: boolean; queueId?: string; message?: string }> {
    try {
      // Calculate manufacturing time
      const manufacturingDays = GAME_FORMULAS.production.getManufacturingTime(
        30, // Base time of 30 days
        product.techScore || 50, // Product complexity based on tech score
        0 // No efficiency bonus for now
      );

      // Calculate expected completion date
      const expectedCompletion = this.addDaysToGameDate(gameDate, manufacturingDays);

      // Create production queue entry
      const queueId = generateId('mfg-');
      const productionEntry: ProductionQueue = {
        id: queueId,
        productId: product.id,
        productName: product.name,
        productionLineId,
        quantity,
        startDate: { ...gameDate },
        expectedCompletionDate: expectedCompletion,
        status: 'in-progress',
        componentsConsumed: new Map(),
        workersAssigned: Math.max(1, Math.ceil(quantity / 1000)),
        progress: 0
      };

      // Consume components from inventory
      console.log(`🏭 Starting manufacturing for ${product.name}: ${quantity} units`);
      let totalComponentValue = 0;
      if (product.designConfig?.components) {
        // First, verify all components are available
        const inventoryState = await inventoryManagementService.initializeInventory([product]);
        
        for (const [category, componentId] of Object.entries(product.designConfig.components)) {
          const componentsNeeded = quantity; // 1:1 ratio
          const component = inventoryState.components.get(componentId);
          if (!component || component.currentStock < componentsNeeded) {
            return {
              success: false,
              message: `Insufficient ${component?.componentName || componentId} stock. Available: ${component?.currentStock || 0}, Needed: ${componentsNeeded}`
            };
          }
        }
        
        // Now consume the components since we know they're all available
        for (const [category, componentId] of Object.entries(product.designConfig.components)) {
          const componentsNeeded = quantity; // 1:1 ratio
          console.log(`📉 Consuming ${componentsNeeded} units of component ${componentId}`);
          try {
            await inventoryManagementService.updateComponentStock(
              componentId,
              -componentsNeeded,
              'consumption'
            );
          } catch (error) {
            // This shouldn't happen since we pre-checked, but handle it gracefully
            console.error(`Failed to consume components for ${product.name}:`, error);
            return {
              success: false,
              message: error instanceof Error ? error.message : 'Failed to consume components'
            };
          }
          
          productionEntry.componentsConsumed.set(componentId, componentsNeeded);
          
          // Track component value for WIP
          const component = inventoryState.components.get(componentId);
          if (component) {
            totalComponentValue += component.unitCost * componentsNeeded;
          }
        }
      }

      // Create work-in-progress entry
      const wipEntry: WorkInProgress = {
        productId: product.id,
        productName: product.name,
        quantity,
        startDate: { ...gameDate },
        expectedCompletion: expectedCompletion,
        progress: 0,
        componentsValue: totalComponentValue,
        laborCost: 0,
        totalValue: totalComponentValue
      };

      // Store entries
      this.productionQueue.set(queueId, productionEntry);
      this.workInProgress.set(product.id, wipEntry);
      
      // console.log('[Manufacturing] ✅ Manufacturing started successfully:', {
      //   queueId,
      //   productId: product.id,
      //   productName: product.name,
      //   quantity,
      //   totalComponentValue,
      //   expectedDays: manufacturingDays,
      //   workersAssigned: productionEntry.workersAssigned
      // });

      return {
        success: true,
        queueId,
        message: `Manufacturing started for ${quantity} units of ${product.name}`
      };
    } catch (error) {
      console.error('Failed to start manufacturing:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to start manufacturing'
      };
    }
  }

  // Process daily manufacturing progress
  async processDailyManufacturing(
    currentDate: { day: number; month: number; year: number },
    products: Product[],
    employees: Employee[]
  ): Promise<{
    completedBatches: Array<{ productId: string; quantity: number }>;
    updatedWIP: WorkInProgress[];
  }> {
    // console.log('[Manufacturing] 📅 Processing daily manufacturing:', {
    //   date: `${currentDate.month}/${currentDate.day}/${currentDate.year}`,
    //   activeQueues: this.productionQueue.size,
    //   wipEntries: this.workInProgress.size
    // });
    
    const completedBatches: Array<{ productId: string; quantity: number }> = [];
    const updatedWIP: WorkInProgress[] = [];

    // Process each production queue entry
    for (const [queueId, production] of this.productionQueue) {
      if (production.status !== 'in-progress') continue;

      // Calculate progress
      const totalDays = this.getDaysBetween(production.startDate, production.expectedCompletionDate);
      const daysElapsed = this.getDaysBetween(production.startDate, currentDate);
      const progress = Math.min(100, (daysElapsed / totalDays) * 100);

      production.progress = progress;
      
      // console.log('[Manufacturing] 📏 Manufacturing progress:', {
      //   queueId,
      //   productId: production.productId,
      //   productName: production.productName,
      //   daysElapsed,
      //   totalDays,
      //   progress: `${progress.toFixed(1)}%`,
      //   expectedCompletion: `${production.expectedCompletionDate.month}/${production.expectedCompletionDate.day}/${production.expectedCompletionDate.year}`
      // });

      // Check if production is complete
      if (this.isDateOnOrAfter(currentDate, production.expectedCompletionDate)) {
        // console.log(`[Manufacturing] 🏭 Manufacturing complete for ${production.productName}: ${production.quantity} units`);
        production.status = 'completed';
        production.actualCompletionDate = { ...currentDate };
        production.progress = 100;

        // Add to completed batches
        completedBatches.push({
          productId: production.productId,
          quantity: production.quantity
        });

        // Calculate unit value based on WIP value
        const wip = this.workInProgress.get(production.productId);
        const unitValue = wip ? wip.totalValue / production.quantity : 0;
        // console.log(`[Manufacturing] 💰 Unit production cost: $${unitValue.toFixed(2)}`);
        
        // Update finished goods inventory with calculated unit value
        console.log(`[Manufacturing] 📦 Adding ${production.quantity} devices to inventory for ${production.productName}`);
        await inventoryManagementService.updateDeviceStock(
          production.productId,
          production.quantity,
          true, // isProduction = true
          unitValue
        );

        // Remove from WIP
        this.workInProgress.delete(production.productId);
        // console.log('[Manufacturing] ✅ Production completed and inventory updated');
      } else {
        // Update WIP progress
        const wip = this.workInProgress.get(production.productId);
        if (wip) {
          wip.progress = progress;
          
          // Calculate accumulated labor cost
          const dailyLaborCost = production.workersAssigned * 100; // $100/day per worker
          wip.laborCost = dailyLaborCost * daysElapsed;
          wip.totalValue = wip.componentsValue + wip.laborCost;
          
          updatedWIP.push(wip);
          
          // console.log('[Manufacturing] 📋 WIP updated:', {
          //   productId: production.productId,
          //   progress: `${progress.toFixed(1)}%`,
          //   componentsValue: wip.componentsValue,
          //   laborCost: wip.laborCost,
          //   totalValue: wip.totalValue
          // });
        }
      }
    }

    // Clean up completed entries after 7 days
    for (const [queueId, production] of this.productionQueue) {
      if (production.status === 'completed' && production.actualCompletionDate) {
        const daysSinceCompletion = this.getDaysBetween(production.actualCompletionDate, currentDate);
        if (daysSinceCompletion > 7) {
          this.productionQueue.delete(queueId);
        }
      }
    }

    // console.log('[Manufacturing] 📊 Daily manufacturing summary:', {
    //   completedBatches: completedBatches.length,
    //   completedUnits: completedBatches.reduce((sum, batch) => sum + batch.quantity, 0),
    //   wipUpdated: updatedWIP.length,
    //   activeQueuesRemaining: Array.from(this.productionQueue.values()).filter(p => p.status === 'in-progress').length
    // });
    
    return { completedBatches, updatedWIP };
  }

  // Get all active production for a product
  getActiveProduction(productId: string): ProductionQueue | null {
    for (const [id, production] of this.productionQueue) {
      if (production.productId === productId && production.status === 'in-progress') {
        return production;
      }
    }
    return null;
  }

  // Get all production queue entries
  getAllProduction(): ProductionQueue[] {
    return Array.from(this.productionQueue.values());
  }

  // Get work in progress for all products
  getWorkInProgress(): WorkInProgress[] {
    return Array.from(this.workInProgress.values());
  }

  // Get WIP value for inventory calculations
  getTotalWIPValue(): number {
    let total = 0;
    for (const wip of this.workInProgress.values()) {
      total += wip.totalValue;
    }
    return total;
  }

  // Helper: Get number of allocated workers
  private getAllocatedWorkers(): number {
    let total = 0;
    for (const production of this.productionQueue.values()) {
      if (production.status === 'in-progress') {
        total += production.workersAssigned;
      }
    }
    return total;
  }

  // Helper: Get factory capacity usage
  private getFactoryCapacityUsage(factoryId: string): number {
    // Get all production lines for this factory
    const lines = factoryProductionService.getFactoryProductionLines(factoryId);
    if (!lines || !Array.isArray(lines)) {
      return 0;
    }
    
    let totalEfficiency = 0;
    let activeLines = 0;
    
    for (const line of lines) {
      if (line.status === 'active') {
        totalEfficiency += line.efficiency;
        activeLines++;
      }
    }
    
    // Average efficiency represents capacity usage
    return activeLines > 0 ? totalEfficiency / activeLines : 0;
  }

  // Helper: Add days to game date
  private addDaysToGameDate(
    date: { day: number; month: number; year: number },
    daysToAdd: number
  ): { day: number; month: number; year: number } {
    let { day, month, year } = date;
    day += daysToAdd;
    
    while (day > 30) {
      day -= 30;
      month += 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
    }
    
    return { day, month, year };
  }

  // Helper: Get days between two game dates
  private getDaysBetween(
    date1: { day: number; month: number; year: number },
    date2: { day: number; month: number; year: number }
  ): number {
    const days1 = date1.year * 360 + date1.month * 30 + date1.day;
    const days2 = date2.year * 360 + date2.month * 30 + date2.day;
    return Math.abs(days2 - days1);
  }

  // Helper: Check if date1 is on or after date2
  private isDateOnOrAfter(
    date1: { day: number; month: number; year: number },
    date2: { day: number; month: number; year: number }
  ): boolean {
    if (date1.year > date2.year) return true;
    if (date1.year < date2.year) return false;
    if (date1.month > date2.month) return true;
    if (date1.month < date2.month) return false;
    return date1.day >= date2.day;
  }

  // Cancel all manufacturing for a discontinued product
  cancelManufacturingForProduct(productId: string): void {
    let entriesCancelled = 0;
    
    // Find and cancel all production queue entries for this product
    for (const [queueId, production] of this.productionQueue) {
      if (production.productId === productId && production.status === 'in-progress') {
        // Mark as cancelled
        production.status = 'cancelled';
        production.actualCompletionDate = {
          day: new Date().getDate(),
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear()
        };
        entriesCancelled++;
        
        // Return components to inventory (if any were consumed)
        // Note: In the current implementation, components are consumed at the start
        // so we might want to return them if production is cancelled mid-way
        // This would require tracking consumed components per production entry
      }
    }
    
    // Remove from work in progress
    if (this.workInProgress.has(productId)) {
      this.workInProgress.delete(productId);
    }
    
    if (entriesCancelled > 0) {
      console.log(`Cancelled ${entriesCancelled} manufacturing batch(es) for discontinued product ${productId}`);
    }
  }

  // Clear all manufacturing data (for testing/reset)
  clearAllManufacturing(): void {
    this.productionQueue.clear();
    this.workInProgress.clear();
  }
}

export const manufacturingService = ManufacturingService.getInstance();